import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { type ReactNode } from "react";
import { getFieldLabel } from "routes/Customer/Waste/Manifests/DiscrepancyReviewDialog.helpers";

import {
  MANIFEST_REVIEW_DISCREPANCIES_QUERY,
  type ManifestReviewRow,
  assigneeLabel,
  isActiveDiscrepancy,
} from "./api";

interface ManifestDetailDialogProps {
  row: ManifestReviewRow | null;
  open: boolean;
  onClose: () => void;
  /** Open the assign picker for this manifest. */
  onAssign: () => void;
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

/** A single source's value within a field discrepancy; hidden when that source has no value. */
function SourceValue({ label, value }: { label: string; value: string | null | undefined }) {
  if (value == null || value === "") return null;
  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 76 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * One detail line item. `accent` drives the left border color, by STATUS: `warning` (orange)
 * for UNRESOLVED, `info` (blue) for PENDING and for document reviews awaiting review.
 */
function DetailCard({
  accent,
  title,
  chips,
  children,
}: {
  accent: "warning" | "info";
  title: string;
  chips?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Box sx={{ borderLeft: 3, borderColor: `${accent}.main`, pl: 1.5, py: 0.75 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: "break-word" }}>
          {title}
        </Typography>
        {chips && (
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            {chips}
          </Stack>
        )}
      </Stack>
      {children}
    </Box>
  );
}

export function ManifestDetailDialog({ row, open, onClose, onAssign }: ManifestDetailDialogProps) {
  const { data, loading } = useQuery(MANIFEST_REVIEW_DISCREPANCIES_QUERY, {
    variables: { processedManifestId: row?.processedManifestId ?? "" },
    skip: !open || !row,
  });

  const fieldDiscrepancies = (data?.manifestDiscrepancies ?? []).filter(isActiveDiscrepancy);
  const documentCount = row?.documentIds.length ?? 0;
  // Document review follows the status color rule: orange (needs review) while nobody is
  // on it, blue (pending) once assigned. Reviewed documents never appear here —
  // documentIds only carries needs-review docs. Keyed off the manifest's derived
  // assignee: the queue assigns a manifest as a unit, so it tracks the document's own
  // assignment in practice.
  const documentPending = row?.assignee != null;
  const documentAccent = documentPending ? "info" : "warning";
  const hasNoDetails = !loading && fieldDiscrepancies.length === 0 && documentCount === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{row ? `Manifest ${row.manifestTrackingNumber}` : "Manifest"}</DialogTitle>
      <DialogContent dividers>
        {row && (
          <Stack spacing={1}>
            <Field label="Tenant" value={row.tenantName} />
            <Field label="Facility" value={row.facilityName ?? row.facilityEpaId ?? "—"} />
            <Field label="Status" value={row.status} />
            <Field label="Assignee" value={assigneeLabel(row.assignee)} />

            <Divider sx={{ mt: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Discrepancy details
            </Typography>

            {loading && (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={20} />
              </Stack>
            )}

            {hasNoDetails && (
              <Typography variant="body2" color="text.secondary">
                No open discrepancies.
              </Typography>
            )}

            {/* The list scrolls within a bounded height so a manifest with many discrepancies
                never pushes the dialog past the viewport. */}
            {!loading && (fieldDiscrepancies.length > 0 || documentCount > 0) && (
              <Stack spacing={1} sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5, pb: 1.5 }}>
                {fieldDiscrepancies.map((discrepancy) => {
                  // Color by STATUS, not type: orange (warning) = UNRESOLVED, blue (info) = PENDING.
                  const accent = discrepancy.status === "PENDING" ? "info" : "warning";
                  return (
                    <DetailCard
                      key={discrepancy.id}
                      accent={accent}
                      title={getFieldLabel(discrepancy.field)}
                      chips={
                        <>
                          <Chip label={discrepancy.severity} size="small" variant="outlined" />
                          <Chip
                            label={discrepancy.status}
                            size="small"
                            color={accent}
                            variant="outlined"
                          />
                        </>
                      }
                    >
                      <Stack sx={{ mt: 0.5 }}>
                        <SourceValue label="e-Manifest" value={discrepancy.eManifestValue} />
                        <SourceValue label="Vendor" value={discrepancy.vendorValue} />
                        <SourceValue label="Document" value={discrepancy.manifestDocumentValue} />
                      </Stack>
                    </DetailCard>
                  );
                })}

                {documentCount > 0 && (
                  <DetailCard
                    accent={documentAccent}
                    title={
                      documentCount === 1
                        ? "Document review"
                        : `Document reviews (${documentCount})`
                    }
                    chips={
                      <Chip
                        label={documentPending ? "PENDING" : "NEEDS REVIEW"}
                        size="small"
                        color={documentAccent}
                        variant="outlined"
                      />
                    }
                  >
                    <Typography variant="caption" color="text.secondary">
                      AI-ingested document awaiting review.
                    </Typography>
                  </DetailCard>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onAssign} disabled={!row}>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
